import { Analytics, Upload, ViewList } from '@mui/icons-material'
import { Box, Button, Card, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import TableHeader from '../../common/TableHeader'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { leadingZeroFormatter } from '../../../utils/utilities'
import CustomTableColumnLabel from '../../common/CustomTableColumnLabel'
import api from '../../../service/api'
import { exportQuizzesAuditReport } from '../../../utils/auditing-export-utilities'
import NoDatImg from '../../../assets/no-data.svg'
import LineBarGraph from '../../common/LineBarGraph'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import CustomSwitch from '../../common/CustomSwitch'


const QuizzesStats = () => {

    const [searchParams, setSearchParams] = useSearchParams( { sortByDept: "0", searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "0", sortByAttemptsCount: "0", sortByQuizzesCount: "0" } )
    const month = searchParams.get( 'month' ) || ""
    const view = searchParams.get( 'view' ) || "tabular"
    const searchText = searchParams.get( 'searchText' ) || ""
    const sortByName = searchParams.get( 'sortByName' ) || "0"
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"
    const sortByQuizzesCount = searchParams.get( 'sortByQuizzesCount' ) || "0"
    const sortByAttemptsCount = searchParams.get( 'sortByAttemptsCount' ) || "0"
    const sortByDept = searchParams.get( 'sortByDept' ) || "0"

    const hasFilters = !( !month && selectedDepartment === "all" && selectedInstitute === "all" && !searchText )

    const [page, setPage] = useState( 0 )
    const [loading, setLoading] = useState( true )
    const [graphData, setGraphData] = useState( [] )
    const [statsError, setStatsError] = useState( null )
    const [graphError, setGraphError] = useState( null )
    const [facultyStats, setFacultyStats] = useState( [] )
    const [showMonthPicker, setShowMonthPicker] = useState( false )
    const [loadingGraphData, setLoadingGraphData] = useState( true )
    const [last12MonthsStats, setLast12MonthsStats] = useState( null )
    const [sortedFacultyStats, setSortedFacultyStats] = useState( [] )
    const [facultiesWithZeroQuizzes, setFacultiesWithZeroQuizzes] = useState( [] )
    const [loadingZeroQuizzesFaculties, setLoadingZeroQuizzesFaculties] = useState( false )
    const [showFacultiesWithZeroQuizzes, setShowFacultiesWithZeroQuizzes] = useState( false )
    const [rowsPerPage, setRowsPerPage] = useState( +( localStorage.getItem( 'adminAuditRowsPerPage' ) || 50 ) )

    const { palette, table, border } = useTheme()

    const navigate = useNavigate()


    const setSearchParamValue = ( key, val ) => {
        setSearchParams( prev => {
            prev.set( key, val )
            return prev
        }, { replace: true } )
    }

    const removeAppliedFilters = () => {
        setSearchParams( prev => {
            prev.set( 'searchText', "" )
            prev.set( 'month', "" )
            prev.set( 'selectedDepartment', "all" )
            prev.set( 'selectedInstitute', "all" )
            return prev
        }, { replace: true } )
    }

    const handleFilterChange = ( e, newVal ) => {
        if ( newVal === '' || newVal === 'last30days' ) {
            setSearchParamValue( 'month', newVal )
        } else {
        }
    }

    const handleViewChange = ( e, newVal ) => {
        if ( newVal )
            setSearchParamValue( 'view', newVal )
    }

    const handleMonthChange = newDate => {
        const date = new Date( newDate )
        if ( date !== 'Invalid Date' ) {
            setSearchParamValue( 'month', `${date.getFullYear()}-${leadingZeroFormatter.format( date.getMonth() + 1 )}` )
            setShowMonthPicker( false )
        }
    }

    const searchFilterFunc = item => {
        const searchValue = new RegExp( searchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            const doesMatchDepartment = ( item.legacy_system_data && +legacy_department_id === +item?.department_id ) || ( !item.legacy_system_data && +department_id === +item?.department_id )
            return doesMatchDepartment && ( item?.department?.match( searchValue ) || item?.faculty_name?.match( searchValue ) || item.created_by?.match( searchValue ) )
        } else
            return item?.department?.match( searchValue ) || item?.faculty_name?.match( searchValue ) || item.created_by?.match( searchValue )
    }

    const columns =
        [
            { id: 1, name: 'Sl. No.', align: "center", padding: "10px 20px 10px 40px" },
            { id: 2, name: <CustomTableColumnLabel data={sortedFacultyStats} setData={setSortedFacultyStats} label="Faculty" setSortState={setSearchParamValue} sortKey={"faculty_name"} sortName="sortByName" sortFlag={sortByName} sortValueType="string" />, minWidth: "150px", padding: "10px 20px" },
            { id: 3, name: 'AUID', align: "center", padding: "10px 20px" },
            { id: 6, name: <CustomTableColumnLabel data={sortedFacultyStats} setData={setSortedFacultyStats} label="Department" setSortState={setSearchParamValue} sortKey={"department"} sortName="sortByDept" sortFlag={sortByDept} sortValueType="string" />, minWidth: "150px", padding: "10px 20px" },
            { id: 4, align: "center", style: { align: "center" }, name: <CustomTableColumnLabel align="center" data={sortedFacultyStats} setData={setSortedFacultyStats} label="Quiz Instances" setSortState={setSearchParamValue} sortKey={"count"} sortName="sortByQuizzesCount" sortFlag={sortByQuizzesCount} sortValueType="number" />, minWidth: "150px", padding: "10px 20px" },
            { id: 5, align: "center", style: { align: "center" }, name: <CustomTableColumnLabel align="center" data={sortedFacultyStats} setData={setSortedFacultyStats} label="Attempts" setSortState={setSearchParamValue} sortKey={"attempts"} sortName="sortByAttemptsCount" sortFlag={sortByAttemptsCount} sortValueType="number" />, minWidth: "150px", padding: "10px 40px 10px 20px" },
        ]

    const handleShowFacultiesWithZeroQuizzesChange = async e => {
        if ( e.target.checked ) {
            setLoadingZeroQuizzesFaculties( true )
            try {
                const queries = {
                    institute: selectedInstitute !== "all" ? selectedInstitute : "",
                    department: selectedDepartment !== "all" ? selectedDepartment : ""
                }
                const { data: { data: facultiesList } } = await api.audit.fetchFacultiesUnderAuditor( queries )
                const facultiesIdWithExams = facultyStats.map( i => i.created_by )
                const facultiesIdWithoutExams = facultiesList.filter( i => !facultiesIdWithExams.includes( i.session_id ) ).map( i => {
                    return {
                        count: 0,
                        attempts: 0,
                        faculty_name: i.employee_name,
                        department: i.branch_name,
                        department_id: i.branch_id,
                        institute: i.institute_name_short,
                        created_by: i.session_id,
                    }
                } )
                setSortedFacultyStats( [...facultiesIdWithoutExams, ...facultyStats] )
                setShowFacultiesWithZeroQuizzes( true )
            } catch ( err ) {
                console.log( err )
                toast( "Oops! Something went wrong while fetching faculties with no exams." )
            }
        } else {
            setSortedFacultyStats( facultyStats )
            setShowFacultiesWithZeroQuizzes( false )
        }
        setLoadingZeroQuizzesFaculties( false )
    }

    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: { facultyStats, last12MonthsStats = {} } } } = await api.audit.getQuizzesStats( { month, institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            setFacultyStats( facultyStats )
            setSortedFacultyStats( facultyStats )
            setLast12MonthsStats( Object.keys( last12MonthsStats ).length > 0 ? last12MonthsStats : null )
            setStatsError( null )
        } catch ( err ) {
            setStatsError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the quizzes audit data." } )
        } finally {
            setLoading( false )
        }
    }, [month, selectedInstitute] )


    const getGraphData = useCallback( async () => {
        try {
            setLoadingGraphData( true )
            const institute = selectedInstitute !== "all" ? selectedInstitute : ""
            const department = selectedDepartment !== "all" ? selectedDepartment : ""
            const { data: graphDetails } = await api.audit.getQuizGraphData( { month, institute, department } )
            setGraphData( graphDetails.data )
            setGraphError( null )
        } catch ( err ) {
            setGraphError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the quizzes graph data." } )
        } finally {
            setLoadingGraphData( false )
        }
    }, [month, selectedInstitute, selectedDepartment] )

    useEffect( () => {
        setFacultiesWithZeroQuizzes( [] )
        setShowFacultiesWithZeroQuizzes( false )
    }, [month] )

    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        getGraphData()
    }, [getGraphData] )


    return (
        <Card sx={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>
            <Box padding="20px" display="flex" gap="20px" justifyContent="space-between" >
                <Box>
                    <Typography color="primaryDark.main" fontSize="16px" variant='h6'>Quizzes Statistics</Typography>
                    {month === "" && view === 'tabular' && <Box display="flex" alignItems="center" gap="10px">
                        <Typography color="primaryDark.main" fontSize="12px" variant='subtitle2'>Show faculty with zero quizzes</Typography>
                        <CustomSwitch checked={showFacultiesWithZeroQuizzes} onChange={handleShowFacultiesWithZeroQuizzesChange} />
                        {loadingZeroQuizzesFaculties && <DataLoadingSpinner size="12px" padding='0' />}
                    </Box>}
                </Box>
                <Box position="relative" display="flex" gap="20px" alignItems="center">
                    <ToggleButtonGroup size='small' value={view} exclusive onChange={handleViewChange} >
                        <Tooltip title="Tabular view">
                            <ToggleButton value='tabular' sx={{ padding: "6px", '&.Mui-selected': { background: palette.primary.light, color: "white", '&:hover': { background: palette.primary.light } } }} size="small">
                                <ViewList fontSize='small' />
                            </ToggleButton>
                        </Tooltip>
                        <Tooltip title="Graphical view">
                            <ToggleButton sx={{ padding: "6px", '&.Mui-selected': { background: palette.primary.light, color: "white", '&:hover': { background: palette.primary.light } } }} value='graphical' size="small">
                                <Analytics fontSize='small' />
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup size='small' onChange={handleFilterChange} exclusive value={month || ""}>
                        {view === 'tabular' && <ToggleButton size='small' sx={{ textTransform: "capitalize", padding: "4.5px 10px" }} value="">All</ToggleButton>}
                        <ToggleButton size='small' sx={{ textTransform: "capitalize", padding: "4.5px 10px" }} value="last30days">Last 30 days</ToggleButton>
                        <ToggleButton size='small' sx={{ textTransform: "capitalize", padding: "4.5px 10px" }} value="-" selected={Boolean( month.match( /^(\d{4}-\d{2})$/ ) )}>
                            {month && month !== 'last30days' ? month : "Select month"}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    open={showMonthPicker}
                                    onClose={() => setShowMonthPicker( false )}
                                    id="startdatetime"
                                    slotProps={{
                                        textField: {
                                            onClick: () => setShowMonthPicker( true )
                                        }
                                    }}
                                    slots={{
                                        openPickerButton: () => null,
                                    }}
                                    sx={{ opacity: 0, position: "absolute", inset: 0, fontSize: "14px", '& input': { padding: "5px 10px", cursor: "pointer" } }}
                                    // value={dayjs( endDateTime )}
                                    views={['year', 'month']}
                                    onMonthChange={handleMonthChange}
                                />
                            </LocalizationProvider>
                        </ToggleButton>
                    </ToggleButtonGroup>


                    <Button size="small" onClick={() => navigate( `list` )} variant="contained" disableElevation sx={{ height: "33.5px", textTransform: "capitalize", background: "#FFE0E0", border: `1px solid #FF6C8F`, color: "#FF736F", '&:hover': { background: "#FFE0E0", color: "#FF736F" } }} >View Quizzes</Button>
                    <Button size="small" onClick={() => exportQuizzesAuditReport( sortedFacultyStats, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-quizzes-audit-report` )} variant="contained" disableElevation sx={{ height: "33.5px", background: `linear-gradient(200.17deg, #1CB5B5 -47.65%, #105958 167.67%)`, color: "#fff", '&:hover': { background: `linear-gradient(200.17deg, #1CB5B5 -47.65%, #105958 167.67%)`, color: "#fff" } }} endIcon={<Upload fontSize='small' />} >Export</Button>
                </Box>
            </Box >
            {!statsError && !loading && view === 'tabular' && ( sortedFacultyStats.filter( searchFilterFunc ).length > 0 || facultiesWithZeroQuizzes.filter( searchFilterFunc ).length > 0 ) && <Box overflow="hidden" bgcolor={palette.contentBg} border={border[1]} borderRadius="10px" margin="0 20px 20px 20px">
                <TableContainer sx={{ overflow: "auto", maxHeight: "500px" }} className='custom-scrollbar' >
                    <Table stickyHeader>
                        <TableHeader columns={columns} />
                        <TableBody>
                            {sortedFacultyStats.filter( searchFilterFunc ).slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage ).map( ( row, index ) => {
                                return <TableRow className='table-row' key={row.created_by + index}>
                                    <TableCell align={columns[0].align || "left"} sx={{ fontSize: "12px", padding: columns[0].padding || "10px 20px", color: table.color }}><strong>{index + 1}</strong></TableCell>
                                    <TableCell sx={{ fontSize: "12px", padding: columns[1].padding || "10px 20px", color: table.color, textTransform: "capitalize" }}><strong>{row?.faculty_name?.toLowerCase() || row?.created_by}</strong></TableCell>
                                    <TableCell align={columns[2].align || "left"} sx={{ fontSize: "12px", padding: columns[2].padding || "10px 20px", color: table.color }}><strong>{row.created_by}</strong></TableCell>
                                    <TableCell align={columns[3].align || "left"} sx={{ fontSize: "12px", padding: columns[3].padding || "10px 20px", color: table.color, textTransform: "capitalize" }}><strong> <small>{row.institute} - {row.department?.toUpperCase()}</small></strong></TableCell>
                                    <TableCell align={columns[4].align || "left"} sx={{ fontSize: "12px", padding: columns[3].padding || "10px 20px", color: table.color }}><strong>{row.count}</strong></TableCell>
                                    <TableCell align={columns[5].align || "left"} sx={{ fontSize: "12px", padding: columns[4].padding || "10px 20px", color: table.color }}><strong>{row.attempts}</strong></TableCell>
                                </TableRow>
                            } )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    sx={{ background: table.headerBg, position: "sticky", bottom: 0, flexShrink: 0 }}
                    rowsPerPageOptions={[50, 100, 150, 200]}
                    component="div"
                    count={sortedFacultyStats.filter( searchFilterFunc ).length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={( event, val ) => setPage( val )}
                    onRowsPerPageChange={( event ) => {
                        setRowsPerPage( parseInt( event.target.value, 10 ) )
                        localStorage.setItem( 'adminAuditRowsPerPage', event.target.value )
                        setPage( 0 )
                    }}
                />
            </Box>}
            {statsError && <TryAgainMessageBlock err={statsError.message} loading={loading} getData={getData} code={statsError.code} />}
            {!statsError && !loading && sortedFacultyStats.filter( searchFilterFunc ).length === 0 && facultiesWithZeroQuizzes.filter( searchFilterFunc ).length === 0 && <Box flexDirection="column" padding="20px" display="flex" alignItems="center" justifyContent="center" gap="20px">
                <Box component="img" src={NoDatImg} height="120px" alt="No quizzes in last 30 days" />
                <Typography textAlign="center" variant='subtitle2'>No quizzes were found {hasFilters && "for applied filters!"}</Typography>
                {( facultyStats.length > 0 || hasFilters ) && <Button onClick={removeAppliedFilters} sx={{ textTransform: "capitalize" }} color="secondary" variant="contained" disableElevation>Remove filters</Button>}
            </Box>}
            {
                !loading && !loadingGraphData && view !== 'tabular' && sortedFacultyStats.filter( searchFilterFunc ).length > 0 &&
                <Box display="flex" flexGrow={1} minHeight="400px">
                    {!graphError ? <LineBarGraph showMenuBar={false} barLegend='Quiz attempts' yAxisName="Quiz Instances" xAxisName="Date" y2AxisName="Attempts" lineLegend='Quiz instances created' barValuesKey="attempts" barData={graphData} dateformat={"%Y-%m-%d"} xAxisValuesKey={'date'} lineValuesKey={'quizzes'} lineData={graphData} />
                        : <TryAgainMessageBlock err={graphError.message} loading={loadingGraphData} getData={getGraphData} code={graphError.code} />}
                </Box>
            }
            {( loading || loadingGraphData ) && !statsError && !graphError && <DataLoadingSpinner waitingMessage="Loading quizzes audit report please..." />}
        </Card >
    )
}

export default QuizzesStats
